/**
 * Dealer's Choice Store
 *
 * Manages the Dealer's Choice gamified challenge discovery game.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  DealersChoiceGame,
  DealersChoiceCard,
  DCHistoryEntry,
} from '../types'
import { dealersChoiceApi } from '../api'

interface DealersChoiceState {
  currentGame: DealersChoiceGame | null
  dealtCards: DealersChoiceCard[]
  selectedCardIndex: number | null
  gameStatus: 'idle' | 'dealing' | 'revealed' | 'accepted' | 'passed'
  xpEarned: number | null

  // Cooldown
  onCooldown: boolean
  cooldownEndsAt: string | null
  remainingMs: number | null

  // History
  history: DCHistoryEntry[]

  // UI
  isLoading: boolean
  error: string | null

  // Actions
  dealCards: () => Promise<void>
  acceptCard: (cardIndex: number) => Promise<void>
  passCards: () => Promise<void>
  checkCooldown: () => Promise<void>
  fetchHistory: () => Promise<void>
  reset: () => void
}

export const useDealersChoiceStore = createWithEqualityFn<DealersChoiceState>((set, get) => ({
  currentGame: null,
  dealtCards: [],
  selectedCardIndex: null,
  gameStatus: 'idle',
  xpEarned: null,
  onCooldown: false,
  cooldownEndsAt: null,
  remainingMs: null,
  history: [],
  isLoading: false,
  error: null,

  dealCards: async () => {
    set({ isLoading: true, error: null, gameStatus: 'dealing' })
    const response = await dealersChoiceApi.dealCards()
    if (response.success && response.data) {
      const game = response.data
      set({
        currentGame: game,
        dealtCards: game.cards,
        gameStatus: 'revealed',
        selectedCardIndex: null,
        xpEarned: null,
        isLoading: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to deal cards',
        gameStatus: 'idle',
        isLoading: false,
      })
    }
  },

  acceptCard: async (cardIndex) => {
    const { currentGame } = get()
    if (!currentGame) return

    set({ isLoading: true, error: null })
    const response = await dealersChoiceApi.acceptCard(currentGame.id, cardIndex)
    if (response.success && response.data) {
      const game = response.data
      set({
        currentGame: game,
        selectedCardIndex: cardIndex,
        gameStatus: 'accepted',
        xpEarned: game.xpEarned ?? null,
        onCooldown: true,
        cooldownEndsAt: game.cooldownEndsAt ?? null,
        isLoading: false,
      })
    } else {
      set({ error: response.error ?? 'Failed to accept card', isLoading: false })
    }
  },

  passCards: async () => {
    const { currentGame } = get()
    if (!currentGame) return

    set({ isLoading: true, error: null })
    const response = await dealersChoiceApi.passCards(currentGame.id)
    if (response.success && response.data) {
      set({
        currentGame: response.data,
        gameStatus: 'passed',
        onCooldown: true,
        cooldownEndsAt: response.data.cooldownEndsAt ?? null,
        isLoading: false,
      })
    } else {
      set({ error: response.error ?? 'Failed to pass', isLoading: false })
    }
  },

  checkCooldown: async () => {
    const response = await dealersChoiceApi.getCooldownStatus()
    if (response.success && response.data) {
      set({
        onCooldown: response.data.onCooldown,
        cooldownEndsAt: response.data.endsAt ?? null,
        remainingMs: response.data.remainingMs ?? null,
      })
    }
  },

  fetchHistory: async () => {
    set({ isLoading: true })
    const response = await dealersChoiceApi.getDCHistory()
    if (response.success && response.data) {
      set({ history: response.data, isLoading: false })
    } else {
      set({ isLoading: false })
    }
  },

  reset: () => {
    set({
      currentGame: null,
      dealtCards: [],
      selectedCardIndex: null,
      gameStatus: 'idle',
      xpEarned: null,
      isLoading: false,
      error: null,
    })
  },
}))

export const selectCurrentGame = (state: DealersChoiceState) => state.currentGame
export const selectDealtCards = (state: DealersChoiceState) => state.dealtCards
export const selectGameStatus = (state: DealersChoiceState) => state.gameStatus
export const selectDCHistory = (state: DealersChoiceState) => state.history
export const selectOnCooldown = (state: DealersChoiceState) => state.onCooldown
